import { factSide } from './facts.js';
/** A case's other insurance records as peers: EVERY record, a hidden one (DO NOT USE, Duplicate...) included, since a file may still be its
 *  letter (Codex 10-21 r2 #2). Its side: the party field, else PLT / DEF in its name; neither = both sides (a peer either way). */
export function lorPeersOf(records, selfId) {
    const out = [];
    for (const x of records) {
        if (x.Id.slice(0, 15) === selfId.slice(0, 15))
            continue;
        const carrier = x.litify_ext__Insurance_Company_Party__r?.Name ?? null;
        if (!carrier)
            continue;
        const party = x.Defendant_or_Plaintiff__c ?? '';
        const name = String(x.Name ?? '').toUpperCase();
        const sides = party === 'Plaintiff' ? ['PLT'] : party === 'Defendant' || party === 'Other Party' ? ['DEF']
            : /(^|[^A-Z])PLT([^A-Z]|$)/.test(name) ? ['PLT'] : /(^|[^A-Z])DEF([^A-Z]|$)/.test(name) ? ['DEF'] : ['PLT', 'DEF'];
        for (const side of sides)
            out.push({ side: side, carrier });
    }
    return out;
}
const ROBOT_LOR_RE = /-SAIJ-OUT-([A-Z0-9]+(?:-[A-Z0-9]+)*?)-(PLT|DEF)-LOR-/;
// "LOR Ack", "LOR acknowledgment", "acknowledgment of LOR / rep / representation / letter of representation", "acknowledged LOR"; a whole word
// after it, so "acknowledgment of police report" never reads as one (Codex 10-21 #3)
const ACK_RE = /(?:^|[^A-Za-z])(?:LOR[-_ ]?Ack(?:nowledge?ment|nowledged)?|Acknowledge?(?:ment|d)?[-_ ]?(?:of[-_ ]|for[-_ ])?(?:our[-_ ])?(?:LOR|Rep(?:resentation)?|Letter[-_ ]of[-_ ]Rep(?:resentation)?))(?![A-Za-z])/i;
/** a robot letter this long before the claim was first seen still counts (the claim can reach the record after the letter's day) */
export const LOR_ROBOT_GRACE_MS = 3 * 24 * 3600 * 1000;
/** at most this many links on a row, newest first */
export const LOR_EVIDENCE_MAX = 3;
/** words that never identify a carrier on their own (company-form words): skipped when an acknowledgment's carrier key is taken */
const FORM_WORDS = new Set(['INSURANCE', 'INS', 'COMPANY', 'CO', 'CORP', 'CORPORATION', 'INC', 'GROUP', 'MUTUAL', 'CASUALTY', 'PROPERTY', 'AUTOMOBILE',
    'AUTO', 'INDEMNITY', 'FIRE', 'GENERAL', 'ASSOCIATION', 'EXCHANGE', 'OF', 'THE', 'AND', 'LLC', 'LTD', 'UNDERWRITERS', 'SERVICES', 'CLAIMS', 'COUNTY']);
/** carrier first words too common to name one carrier on their own: an acknowledgment then needs the second word too ("State Farm") */
const GENERIC_FIRST = new Set(['STATE', 'FIRST', 'NATIONAL', 'AMERICAN', 'UNITED', 'GENERAL', 'HOME', 'PERSONAL', 'NORTH', 'NORTHERN', 'SOUTH', 'SOUTHERN',
    'WEST', 'WESTERN', 'EAST', 'EASTERN', 'PACIFIC', 'GREAT', 'CENTURY', 'MID', 'SECURITY', 'CALIFORNIA', 'TEXAS', 'CITIZENS', 'MUTUAL', 'INSURANCE', 'AUTO', 'THE']);
const words = (s) => s.toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
/** The robot names a letter's carrier by the carrier's first word, or its first words run together (FREDLOYA, STATEFARM): never a part
 *  of a word ("TES" is not Testline), and three characters at least. */
function robotNamesCarrier(segment, carrier) {
    const seg = words(segment).join('');
    if (seg.length < 3)
        return false;
    const w = words(carrier);
    for (let k = 1; k <= w.length; k++) {
        const joined = w.slice(0, k).join('');
        if (joined === seg)
            return true;
        if (joined.length >= seg.length)
            return false;
    }
    return false;
}
/** An acknowledgment names the carrier: the carrier's first two real words (company-form words skipped), consecutive or run together, or
 *  its one real word when it has only one (four characters at least, never a common word alone). Codex 10-21 r2 #1: a first word alone let
 *  "State_Farm" name "Farm Test Insurance". */
function ackNamesCarrier(name, carrier) {
    const w = words(carrier).filter((x) => !FORM_WORDS.has(x));
    if (!w.length)
        return false;
    const key = w.length >= 2 ? [w[0], w[1]] : GENERIC_FIRST.has(w[0]) || w[0].length < 4 ? null : [w[0]];
    if (!key)
        return false;
    const n = words(name);
    if (n.includes(key.join('')))
        return true;
    for (let i = 0; i + key.length <= n.length; i++)
        if (key.every((k, j) => n[i + j] === k))
            return true;
    return false;
}
/** The evidence for one letter row: the case's matching files, newest first, at most LOR_EVIDENCE_MAX. No side, no carrier = none; no
 *  claim-seen time = no robot letter (it cannot be dated against the claim). peers: the case's other records (shared check). */
export function lorEvidenceFor(r, files, claimSeenAt, peers = []) {
    // the side as the row's actions read it: an Other Party record is DEF (facts.ts factSide; Codex 10-21 r5 #1)
    const side = factSide(r.side);
    if (!side || !r.carrier)
        return [];
    const carrier = r.carrier;
    const seen = claimSeenAt ? Date.parse(claimSeenAt) : NaN;
    const others = peers.filter((p) => !!factSide(p.side) && !!p.carrier);
    const out = [];
    for (const f of files) {
        const m = ROBOT_LOR_RE.exec(f.name);
        if (m) {
            if (m[2] === side && robotNamesCarrier(m[1], carrier) && Number.isFinite(seen) && Date.parse(f.at) >= seen - LOR_ROBOT_GRACE_MS) {
                out.push({ kind: 'robot_lor', fileId: f.id, at: f.at, shared: others.some((p) => factSide(p.side) === side && robotNamesCarrier(m[1], p.carrier)) });
            }
            continue; // a robot letter is never also read as an acknowledgment
        }
        if (ACK_RE.test(f.name) && ackNamesCarrier(f.name, carrier)) {
            out.push({ kind: 'carrier_ack', fileId: f.id, at: f.at, shared: others.some((p) => ackNamesCarrier(f.name, p.carrier)) });
        }
    }
    const sorted = out.sort((a, b) => Date.parse(b.at) - Date.parse(a.at) || (a.fileId < b.fileId ? -1 : 1));
    const shown = sorted.slice(0, LOR_EVIDENCE_MAX);
    // the cap never drops the only file that points at this record alone (Codex 10-21 r2 #3): it takes the last place
    const own = sorted.find((e) => !e.shared);
    if (own && !shown.some((e) => !e.shared))
        shown[shown.length - 1] = own;
    return shown;
}
export const LOR_EVIDENCE_LABEL = { robot_lor: 'Robot LOR', carrier_ack: 'Carrier acknowledgment' };
//# sourceMappingURL=lor_evidence.js.map